export interface OnboardingTargetPrompt {
  spendPerPurchase: number;
  savingsPerPurchase: number;
}

const STORAGE_KEY_PREFIX = "carriapp:onboarding-target-prompt:";

const getStorageKey = (listId: string): string => `${STORAGE_KEY_PREFIX}${listId}`;

const isValidTargetPrompt = (value: unknown): value is OnboardingTargetPrompt => {
  if (!value || typeof value !== "object") return false;

  const candidate = value as Partial<OnboardingTargetPrompt>;
  return (
    typeof candidate.spendPerPurchase === "number" &&
    candidate.spendPerPurchase > 0 &&
    typeof candidate.savingsPerPurchase === "number" &&
    candidate.savingsPerPurchase > 0
  );
};

/**
 * Stores the one-time onboarding target prompt for the list created from the onboarding draft.
 */
export const saveOnboardingTargetPrompt = (
  listId: string,
  targetPrompt: OnboardingTargetPrompt,
): void => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(getStorageKey(listId), JSON.stringify(targetPrompt));
  } catch {
    // Non-critical UI hint. If storage is unavailable, the list still works.
  }
};

/**
 * Reads and removes the onboarding target prompt so it only appears on the first list visit.
 */
export const consumeOnboardingTargetPrompt = (listId: string): OnboardingTargetPrompt | null => {
  if (typeof window === "undefined") return null;

  const storageKey = getStorageKey(listId);

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    window.sessionStorage.removeItem(storageKey);

    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    return isValidTargetPrompt(parsed) ? parsed : null;
  } catch {
    window.sessionStorage.removeItem(storageKey);
    return null;
  }
};
